import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";

function fmt(d) {
  if (!d) return "Something went wrong.";
  if (typeof d === "string") return d;
  if (Array.isArray(d)) return d.map((x) => x?.msg || JSON.stringify(x)).join(" ");
  return d.msg || String(d);
}

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
function googleSignIn() {
  const redirect = window.location.origin + "/app";
  window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirect)}`;
}

function GoogleButton({ testid, label }) {
  return (
    <button
      type="button"
      onClick={googleSignIn}
      data-testid={testid}
      className="w-full border border-border bg-card hover:bg-secondary/50 py-3 px-4 flex items-center justify-center gap-3 transition-colors text-sm font-medium"
    >
      <svg width="18" height="18" viewBox="0 0 24 24" aria-hidden="true">
        <path fill="#EA4335" d="M12 10.2v3.9h5.5c-.24 1.4-1.7 4.1-5.5 4.1-3.3 0-6-2.7-6-6.1s2.7-6.1 6-6.1c1.9 0 3.1.8 3.8 1.5l2.6-2.5C16.9 3.4 14.7 2.4 12 2.4 6.6 2.4 2.2 6.7 2.2 12S6.6 21.6 12 21.6c7 0 9.6-4.9 9.6-8.9 0-.6-.1-1-.2-1.4H12z"/>
      </svg>
      {label}
    </button>
  );
}

function OrDivider() {
  return (
    <div className="flex items-center gap-3 my-6" aria-hidden="true">
      <div className="flex-1 rule-line"></div>
      <span className="text-xs uppercase tracking-widest text-muted-foreground">or</span>
      <div className="flex-1 rule-line"></div>
    </div>
  );
}

export function Login() {
  const [email, setEmail] = useState("");
  const [pw, setPw] = useState("");
  const [busy, setBusy] = useState(false);
  const { login } = useAuth();
  const nav = useNavigate();

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await login(email, pw);
      toast.success("Welcome back.");
      nav("/app");
    } catch (err) {
      toast.error(fmt(err.response?.data?.detail) || err.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <AuthShell title="Welcome back" subtitle="Sign in to continue your journey.">
      <GoogleButton testid="login-google" label="Continue with Google" />
      <OrDivider />
      <form onSubmit={submit} className="space-y-8">
        <div>
          <Label htmlFor="email" className="text-xs uppercase tracking-widest text-muted-foreground">Email</Label>
          <input id="email" data-testid="login-email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)}
            className="notebook-input w-full mt-2" />
        </div>
        <div>
          <Label htmlFor="pw" className="text-xs uppercase tracking-widest text-muted-foreground">Password</Label>
          <input id="pw" data-testid="login-password" type="password" required value={pw} onChange={(e) => setPw(e.target.value)}
            className="notebook-input w-full mt-2" />
        </div>
        <Button type="submit" data-testid="login-submit" disabled={busy} className="w-full rounded-full">
          {busy ? "Signing in…" : "Sign in"}
        </Button>
        <div className="text-sm text-muted-foreground text-center">
          New here?{" "}
          <Link to="/register" data-testid="to-register" className="text-primary underline-serif">Begin the journey</Link>
        </div>
      </form>
    </AuthShell>
  );
}

export function Register() {
  const [form, setForm] = useState({ name: "", email: "", password: "" });
  const [busy, setBusy] = useState(false);
  const { register } = useAuth();
  const nav = useNavigate();

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await register(form);
      toast.success("Welcome. The call is going out.");
      nav("/app");
    } catch (err) {
      toast.error(fmt(err.response?.data?.detail) || err.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <AuthShell title="Answer the call" subtitle="Create your account to begin the eleven weeks.">
      <GoogleButton testid="register-google" label="Continue with Google" />
      <OrDivider />
      <form onSubmit={submit} className="space-y-7">
        <div>
          <Label className="text-xs uppercase tracking-widest text-muted-foreground">Name</Label>
          <input required data-testid="reg-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
            className="notebook-input w-full mt-2" />
        </div>
        <div>
          <Label className="text-xs uppercase tracking-widest text-muted-foreground">Email</Label>
          <input type="email" required data-testid="reg-email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })}
            className="notebook-input w-full mt-2" />
        </div>
        <div>
          <Label className="text-xs uppercase tracking-widest text-muted-foreground">Password</Label>
          <input type="password" required minLength={6} data-testid="reg-password" value={form.password}
            onChange={(e) => setForm({ ...form, password: e.target.value })} className="notebook-input w-full mt-2" />
        </div>
        <p className="text-xs text-muted-foreground italic leading-relaxed">
          One account for participants and leaders. Walk the eleven weeks — and when you're ready
          to lead a group, just create one from the Groups tab.
        </p>
        <Button type="submit" data-testid="reg-submit" disabled={busy} className="w-full rounded-full">
          {busy ? "Creating…" : "Begin the journey"}
        </Button>
        <div className="text-sm text-muted-foreground text-center">
          Already have an account?{" "}
          <Link to="/login" data-testid="to-login" className="text-primary underline-serif">Sign in</Link>
        </div>
      </form>
    </AuthShell>
  );
}

function AuthShell({ title, subtitle, children }) {
  return (
    <div className="min-h-screen parchment">
      <div className="max-w-md mx-auto px-6 py-16">
        <Link to="/" className="text-sm text-muted-foreground hover:text-foreground" data-testid="back-home">← Home</Link>
        <div className="mt-10">
          <h1 className="font-serif text-4xl">{title}</h1>
          <p className="text-muted-foreground mt-2">{subtitle}</p>
        </div>
        <div className="mt-10">{children}</div>
      </div>
    </div>
  );
}
